import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { sb } from '../../lib/supabase';
import { useSupabaseAuth, useAccess } from '../../context/AuthContext';
import Loader from '../../components/Loader';
import { buildTree, flattenTree, filterTree } from '../../lib/taxonomy';
import {
  indexAssignments,
  getEffectiveAssignment,
  profileCanActOnTasks,
  nodeHasGap,
  nodePath,
  sortProfilesForRole,
  initials,
  isBpoRole,
  BPO_OPT_IN_RIGHTS,
} from '../../lib/roleAssignments';

function BpoRightsPanel({ assignment, direct, fromNodeName, saving, onToggle }) {
  if (!assignment) return null;

  return (
    <div className="ra-bpo-rights">
      <div className="ra-bpo-rights-title">BPO workflow rights (this node)</div>
      {!direct && fromNodeName && (
        <div className="ra-bpo-rights-note">
          Inherited from {fromNodeName}. Assign a direct BPO at this node to override.
        </div>
      )}
      <div className="ra-bpo-rights-list">
        {BPO_OPT_IN_RIGHTS.map((right) => {
          const on = !!assignment[right.key];
          return (
            <label
              key={right.key}
              className={`ra-bpo-right-row${!direct ? ' readonly' : ''}`}
              title={right.hint}
            >
              <input
                type="checkbox"
                checked={on}
                disabled={!direct || saving}
                onChange={(e) => onToggle(right.key, e.target.checked)}
              />
              <span className="ra-bpo-right-label">{right.label}</span>
              <span className="ra-bpo-right-state">{on ? 'On' : 'Off'}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

function RolePicker({ roleType, profiles, selectedId, showAll, onSelect, onToggleShowAll, onCancel, onAssign }) {
  const { matching, other } = sortProfilesForRole(profiles, roleType);
  return (
    <div className="ra-assign-picker show">
      {matching.length > 0 ? (
        <>
          <div className="ra-picker-group-label">Matches &quot;{roleType.full_name || roleType.name}&quot;</div>
          {matching.map((p) => (
            <button
              key={p.id}
              type="button"
              className={`ra-resource-option${selectedId === p.id ? ' selected' : ''}`}
              onClick={() => onSelect(p.id)}
            >
              <div className="ra-resource-av">{initials(p.full_name)}</div>
              <div>
                <div className="ra-resource-name">{p.full_name}</div>
                <div className="ra-resource-meta">{p.job_title || '—'}</div>
              </div>
            </button>
          ))}
        </>
      ) : (
        <div className="ra-no-match">No resources with role &quot;{roleType.full_name || roleType.name}&quot; found.</div>
      )}
      {showAll && other.length > 0 && (
        <>
          <div className="ra-picker-group-label">Other resources</div>
          {other.map((p) => (
            <button
              key={p.id}
              type="button"
              className={`ra-resource-option${selectedId === p.id ? ' selected' : ''}`}
              onClick={() => onSelect(p.id)}
            >
              <div className="ra-resource-av">{initials(p.full_name)}</div>
              <div>
                <div className="ra-resource-name">{p.full_name}</div>
                <div className="ra-resource-meta">{p.job_title || '—'}</div>
              </div>
            </button>
          ))}
        </>
      )}
      {!showAll && other.length > 0 && (
        <button type="button" className="ra-show-all" onClick={onToggleShowAll}>
          Show all resources ({other.length} more)
        </button>
      )}
      <div className="ra-picker-actions">
        <button type="button" className="ra-role-btn" onClick={onCancel}>Cancel</button>
        <button type="button" className="ra-role-btn primary" disabled={!selectedId} onClick={onAssign}>Assign</button>
      </div>
    </div>
  );
}

export default function RoleAssignments() {
  const { profile } = useSupabaseAuth();
  const { isAdmin } = useAccess();
  const orgId = profile?.organisation_id;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [programmes, setProgrammes] = useState([]);
  const [programmeId, setProgrammeId] = useState('');
  const [nodes, setNodes] = useState([]);
  const [functionalRoles, setFunctionalRoles] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [view, setView] = useState('detail');
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [treeFilter, setTreeFilter] = useState('');
  const [openPicker, setOpenPicker] = useState(null);
  const [pickerPerson, setPickerPerson] = useState(null);
  const [showAllFor, setShowAllFor] = useState(null);
  const [gridLevel, setGridLevel] = useState(3);
  const [gridExpanded, setGridExpanded] = useState(new Set());
  const [colMgrOpen, setColMgrOpen] = useState(false);
  const [popover, setPopover] = useState(null);
  const [toast, setToast] = useState(null);
  const popoverRef = useRef(null);

  const showToast = (msg, type = 'error') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const [{ data: progs }, { data: types }, { data: profs }] = await Promise.all([
        sb.from('programmes').select('id, name').eq('organisation_id', orgId).order('name'),
        sb.from('functional_roles').select('*').eq('organisation_id', orgId).order('order_index'),
        sb.from('profiles').select('id, full_name, job_title, status, auth_subject').eq('organisation_id', orgId).order('full_name'),
      ]);
      setProgrammes(progs || []);
      setFunctionalRoles(types || []);
      setProfiles(profs || []);
      if (!programmeId && progs?.length) setProgrammeId(progs[0].id);

      const { data: pubVer } = await sb
        .from('taxonomy_versions')
        .select('id')
        .eq('organisation_id', orgId)
        .eq('status', 'published')
        .order('published_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (pubVer?.id) {
        const { data: nodeRows } = await sb
          .from('org_process_hierarchy')
          .select('id, parent_id, name, code, level, order_index')
          .eq('organisation_id', orgId)
          .eq('taxonomy_version_id', pubVer.id)
          .order('level')
          .order('order_index');
        setNodes(nodeRows || []);
        const rootIds = (nodeRows || []).filter((n) => !n.parent_id).map((n) => n.id);
        setGridExpanded(new Set(rootIds));
      } else {
        setNodes([]);
      }

      if (programmeId || progs?.[0]?.id) {
        const pid = programmeId || progs[0].id;
        const { data: rows } = await sb
          .from('role_assignments')
          .select('*')
          .eq('programme_id', pid)
          .eq('is_active', true);
        setAssignments(rows || []);
      }
    } catch (e) {
      console.error('[RoleAssignments] load failed', e);
      showToast('Failed to load role assignments');
    } finally {
      setLoading(false);
    }
  }, [orgId, programmeId]);

  useEffect(() => { load(); }, [load]);

  const tree = useMemo(() => buildTree(nodes), [nodes]);
  const filteredTree = useMemo(() => filterTree(tree, treeFilter), [tree, treeFilter]);
  const flatNodes = useMemo(() => flattenTree(tree), [tree]);
  const nodesById = useMemo(() => new Map(flatNodes.map((n) => [n.id, n])), [flatNodes]);
  const assignmentIndex = useMemo(() => indexAssignments(assignments), [assignments]);
  const profilesById = useMemo(() => new Map(profiles.map((p) => [p.id, p])), [profiles]);
  const gridRoleTypes = useMemo(() => functionalRoles.filter((rt) => rt.show_in_grid), [functionalRoles]);
  const selectedNode = selectedNodeId ? nodesById.get(selectedNodeId) : null;

  async function saveAssignment(nodeId, roleTypeId, profileId) {
    if (!programmeId || !isAdmin) return;
    setSaving(true);
    try {
      await sb.from('role_assignments').delete()
        .eq('programme_id', programmeId)
        .eq('functional_role_id', roleTypeId)
        .eq('process_node_id', nodeId);
      const { error } = await sb.from('role_assignments').insert({
        programme_id: programmeId,
        functional_role_id: roleTypeId,
        process_node_id: nodeId,
        profile_id: profileId,
        is_active: true,
      });
      if (error) throw error;
      const { data } = await sb.from('role_assignments').select('*')
        .eq('programme_id', programmeId).eq('is_active', true);
      setAssignments(data || []);
      setOpenPicker(null);
      setPopover(null);
      setPickerPerson(null);
      showToast('Assignment saved', 'success');
    } catch (e) {
      console.error('[RoleAssignments] save failed', e);
      showToast('Failed to save assignment');
    } finally {
      setSaving(false);
    }
  }

  async function clearAssignment(nodeId, roleTypeId) {
    if (!programmeId || !isAdmin) return;
    setSaving(true);
    try {
      const { error } = await sb.from('role_assignments').delete()
        .eq('programme_id', programmeId)
        .eq('functional_role_id', roleTypeId)
        .eq('process_node_id', nodeId);
      if (error) throw error;
      setAssignments((prev) => prev.filter(
        (a) => !(a.process_node_id === nodeId && a.functional_role_id === roleTypeId)
      ));
      setPopover(null);
      showToast('Assignment removed', 'success');
    } catch (e) {
      showToast('Failed to remove assignment');
    } finally {
      setSaving(false);
    }
  }

  async function updateBpoRights(nodeId, roleTypeId, field, value) {
    if (!programmeId || !isAdmin) return;
    const direct = assignments.find(
      (a) => a.process_node_id === nodeId
        && a.functional_role_id === roleTypeId
        && a.is_active
    );
    if (!direct) return;

    setSaving(true);
    try {
      const patch = { [field]: value };
      const { error } = await sb.from('role_assignments').update(patch).eq('id', direct.id);
      if (error) throw error;
      setAssignments((prev) => prev.map((a) => (
        a.id === direct.id ? { ...a, ...patch } : a
      )));
    } catch (e) {
      console.error('[RoleAssignments] rights update failed', e);
      showToast('Failed to update BPO rights');
    } finally {
      setSaving(false);
    }
  }

  async function toggleGridColumn(roleTypeId, show) {
    if (!isAdmin) return;
    const { error } = await sb.from('functional_roles').update({ show_in_grid: show }).eq('id', roleTypeId);
    if (error) {
      showToast('Failed to update columns');
      return;
    }
    setFunctionalRoles((prev) => prev.map((rt) => (rt.id === roleTypeId ? { ...rt, show_in_grid: show } : rt)));
  }

  function renderTreeNode(node) {
    const gap = nodeHasGap(node.id, functionalRoles, assignmentIndex, nodesById, profilesById);
    return (
      <div key={node.id}>
        <button
          type="button"
          className={`ra-tree-row${selectedNodeId === node.id ? ' selected' : ''}`}
          style={{ paddingLeft: 10 + (node.level - 1) * 12 }}
          onClick={() => { setSelectedNodeId(node.id); setOpenPicker(null); }}
        >
          <span className="ra-tree-ic">L{node.level}</span>
          <span className="ra-tree-label">{node.name}</span>
          {gap && <span className="ra-tree-gap" title="Missing role" />}
        </button>
        {(node.children || []).map(renderTreeNode)}
      </div>
    );
  }

  const gridRows = useMemo(() => {
    const walk = (node, depth = 0) => {
      if (node.level > gridLevel) return [];
      const rows = [{ node, depth }];
      if (gridExpanded.has(node.id)) {
        for (const child of node.children || []) {
          rows.push(...walk(child, depth + 1));
        }
      }
      return rows;
    };
    return tree.flatMap((n) => walk(n));
  }, [tree, gridLevel, gridExpanded]);

  if (loading) return <Loader text="Loading role assignments…" />;

  if (!isAdmin) {
    return (
      <div className="content">
        <div className="empty" style={{ marginTop: 40 }}>Company admin access required.</div>
      </div>
    );
  }

  return (
    <div className="ra-shell">
      <div className="ra-page-head">
        <div className="ra-page-head-top">
          <div>
            <div className="ra-page-eyebrow">Admin · Roles</div>
            <div className="ra-page-title">Role Assignments</div>
          </div>
          <div className="ra-view-toggle">
            <button type="button" className={`ra-view-pill${view === 'detail' ? ' on' : ''}`} onClick={() => setView('detail')}>Detail</button>
            <button type="button" className={`ra-view-pill${view === 'grid' ? ' on' : ''}`} onClick={() => setView('grid')}>Grid</button>
          </div>
        </div>
        <div className="ra-page-sub">
          <select className="ra-programme-select" value={programmeId} onChange={(e) => setProgrammeId(e.target.value)}>
            {programmes.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          {' · '}Most granular assignment wins; unassigned nodes inherit from nearest ancestor.
        </div>
      </div>

      {!nodes.length ? (
        <div className="content"><div className="empty">Publish a process taxonomy (ADMIN-02) before assigning roles.</div></div>
      ) : view === 'detail' ? (
        <div className="ra-body-wrap">
          <div className="ra-tree-panel">
            <div className="ra-tree-panel-hdr">
              <div className="ra-tree-panel-title">Process taxonomy</div>
              <input className="ra-tree-search" placeholder="Filter…" value={treeFilter} onChange={(e) => setTreeFilter(e.target.value)} />
            </div>
            <div className="ra-tree-body">{filteredTree.map(renderTreeNode)}</div>
          </div>
          <div className="ra-detail-panel">
            {!selectedNode ? (
              <div className="ra-empty-state">Select a process node</div>
            ) : (
              <>
                <div className="ra-detail-hdr">
                  <span className="ra-detail-badge">L{selectedNode.level}</span>
                  <div className="ra-detail-name">{selectedNode.name}</div>
                  <div className="ra-detail-path">{nodePath(selectedNode.id, nodesById)}</div>
                </div>
                {functionalRoles.map((rt) => {
                  const eff = getEffectiveAssignment(selectedNode.id, rt.id, assignmentIndex, nodesById);
                  const person = eff ? profilesById.get(eff.assignment.profile_id) : null;
                  return (
                    <div key={rt.id} className="ra-role-section">
                      <div className="ra-role-section-hdr">
                        <div className="ra-role-section-title">
                          {rt.full_name || rt.name}
                          {rt.is_delegating && <span className="ra-delegating-tag">Delegating</span>}
                        </div>
                        <button type="button" className="ra-role-btn" disabled={saving} onClick={() => {
                          setOpenPicker(openPicker === rt.id ? null : rt.id);
                          setPickerPerson(null);
                          setShowAllFor(null);
                        }}>
                          {eff?.direct ? 'Change' : '+ Assign direct'}
                        </button>
                      </div>
                      <div className="ra-role-section-body">
                        {person && !profileCanActOnTasks(person) ? (
                          <div className="ra-gap-note">
                            {person.full_name} ({person.status || 'named'}) cannot act on tasks yet — routed to the PM until invited.
                          </div>
                        ) : null}
                        {person ? (
                          <div className="ra-role-card">
                            <div className="ra-role-av">{initials(person.full_name)}</div>
                            <div className="ra-role-info">
                              <div className="ra-role-name-txt">{person.full_name}</div>
                              <div className="ra-role-meta">{person.job_title || '—'}</div>
                            </div>
                            {eff.direct
                              ? <span className="ra-scope-tag">Direct</span>
                              : <span className="ra-inherited-tag">Inherited from {eff.fromNode.name}</span>}
                          </div>
                        ) : (
                          <div className="ra-gap-note">No {rt.full_name || rt.name} assigned here or on any ancestor. Tasks fall back to the PM.</div>
                        )}
                        {isBpoRole(rt) && person && eff && (
                          <BpoRightsPanel
                            assignment={eff.assignment}
                            direct={eff.direct}
                            fromNodeName={eff.direct ? null : eff.fromNode?.name}
                            saving={saving}
                            onToggle={(field, value) => updateBpoRights(selectedNode.id, rt.id, field, value)}
                          />
                        )}
                        {openPicker === rt.id && (
                          <RolePicker
                            roleType={rt}
                            profiles={profiles}
                            selectedId={pickerPerson}
                            showAll={showAllFor === rt.id}
                            onSelect={setPickerPerson}
                            onToggleShowAll={() => setShowAllFor(showAllFor === rt.id ? null : rt.id)}
                            onCancel={() => setOpenPicker(null)}
                            onAssign={() => saveAssignment(selectedNode.id, rt.id, pickerPerson)}
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="ra-grid-panel">
          <div className="ra-grid-toolbar">
            <span className="ra-grid-toolbar-label">Show up to</span>
            {[3, 2, 1].map((l) => (
              <button key={l} type="button" className={`ra-grid-filter-btn${gridLevel === l ? ' on' : ''}`} onClick={() => setGridLevel(l)}>L{l}</button>
            ))}
            <button type="button" className="ra-grid-filter-btn" onClick={() => setColMgrOpen((v) => !v)}>⊞ Columns</button>
            {colMgrOpen && (
              <div className="ra-col-mgr">
                <div className="ra-col-mgr-title">Role columns shown in grid</div>
                {functionalRoles.map((rt) => (
                  <label key={rt.id} className="ra-col-mgr-row">
                    <input
                      type="checkbox"
                      checked={rt.show_in_grid}
                      onChange={(e) => toggleGridColumn(rt.id, e.target.checked)}
                    />
                    {rt.name}
                  </label>
                ))}
              </div>
            )}
          </div>
          <div className="ra-grid-scroll">
            <table className="ra-grid-table">
              <thead>
                <tr>
                  <th className="ra-node-th">Process node</th>
                  {gridRoleTypes.map((rt) => <th key={rt.id}>{rt.name}</th>)}
                </tr>
              </thead>
              <tbody>
                {gridRows.map(({ node }) => (
                  <tr key={node.id} className={`ra-level-${node.level}`}>
                    <td className="ra-node-td">
                      <div className="ra-node-cell" style={{ paddingLeft: 8 + (node.level - 1) * 12 }}>
                        {(node.children || []).length > 0 ? (
                          <button
                            type="button"
                            className="ra-node-expand"
                            onClick={() => setGridExpanded((prev) => {
                              const next = new Set(prev);
                              if (next.has(node.id)) next.delete(node.id);
                              else next.add(node.id);
                              return next;
                            })}
                          >
                            {gridExpanded.has(node.id) ? '▾' : '▸'}
                          </button>
                        ) : <span className="ra-node-expand leaf" />}
                        <span className="ra-node-label">{node.name}</span>
                      </div>
                    </td>
                    {gridRoleTypes.map((rt) => {
                      const eff = getEffectiveAssignment(node.id, rt.id, assignmentIndex, nodesById);
                      const person = eff ? profilesById.get(eff.assignment.profile_id) : null;
                      const isGap = !eff;
                      return (
                        <td key={rt.id}>
                          <div className="ra-role-cell">
                            {isGap ? (
                              <button
                                type="button"
                                className="ra-cell-gap"
                                onClick={(e) => setPopover({ nodeId: node.id, roleTypeId: rt.id, x: e.clientX, y: e.clientY })}
                              >
                                Assign
                              </button>
                            ) : (
                              <button
                                type="button"
                                className={`ra-cell-filled${eff.direct ? '' : ' inherited'}`}
                                onClick={(e) => setPopover({ nodeId: node.id, roleTypeId: rt.id, x: e.clientX, y: e.clientY })}
                              >
                                <span className="ra-cell-av">{initials(person?.full_name)}</span>
                                <span className="ra-cell-name">{person?.full_name}</span>
                              </button>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {popover && (
        <div
          ref={popoverRef}
          className="ra-grid-popover"
          style={{ top: popover.y + 8, left: Math.min(popover.x, window.innerWidth - 300) }}
        >
          <div className="ra-popover-hdr">
            Assign {functionalRoles.find((r) => r.id === popover.roleTypeId)?.full_name}
            <button type="button" className="ra-popover-close" onClick={() => setPopover(null)}>✕</button>
          </div>
          {sortProfilesForRole(profiles, functionalRoles.find((r) => r.id === popover.roleTypeId) || '').matching
            .concat(sortProfilesForRole(profiles, functionalRoles.find((r) => r.id === popover.roleTypeId) || '').other)
            .slice(0, 6)
            .map((p) => (
              <button
                key={p.id}
                type="button"
                className={`ra-pop-resource${pickerPerson === p.id ? ' selected' : ''}`}
                onClick={() => setPickerPerson(p.id)}
              >
                <span className="ra-pop-av">{initials(p.full_name)}</span>
                <span>{p.full_name}</span>
              </button>
            ))}
          <div className="ra-popover-actions">
            <button type="button" className="ra-role-btn" onClick={() => clearAssignment(popover.nodeId, popover.roleTypeId)}>Unassign direct</button>
            <button type="button" className="ra-role-btn primary" disabled={!pickerPerson || saving} onClick={() => saveAssignment(popover.nodeId, popover.roleTypeId, pickerPerson)}>Assign</button>
          </div>
        </div>
      )}

      {toast && <div className={`toast ${toast.type === 'success' ? 'toast-success' : 'toast-error'}`}>{toast.msg}</div>}
    </div>
  );
}
